// File: App.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { ViewState, PlayerProfile, ProducerSkills, CutsceneScript, IdolRank, CostumeId, Mission } from './types';
import { MainMenu } from './components/MainMenu';
import { PracticeRoom } from './components/PracticeRoom';
import { Stage } from './components/Stage';
import { CityHub } from './components/CityHub';
import { VarietyShow } from './components/VarietyShow';
import { IdolCafe } from './components/IdolCafe';
import { Bathhouse } from './components/Bathhouse'; // NEW
import { HealerClinic } from './components/HealerClinic';
import { SpicyChallenge } from './components/SpicyChallenge';
import { MerchShop } from './components/MerchShop';
import { TourMap } from './components/TourMap';
import { Dashboard } from './components/Dashboard';
import { RecordLabel } from './components/RecordLabel';
import { CutsceneView } from './components/CutsceneView';
import { SkyDrop } from './components/SkyDrop';
import { MusicPlayer } from './components/MusicPlayer';
import { SussieMessenger } from './components/SussieMessenger';
import { NotificationSystem, Notification } from './components/NotificationSystem';
import { StreetFood } from './components/StreetFood';
import { MISSION_POOL } from './components/data/missions';
import { INITIAL_HUNTRIX_CONFIG, INITIAL_PLAYER_CASH, BOBBY_COMMISSION, VICTORY_BONUS_CASH, VICTORY_BONUS_FANS, CITIES, RANKS } from './constants';
import { MusicManager, LyricContext, MusicMixMode } from './components/audio/GameAudioManager';
import { SONGS } from './components/data/songs';
import { Song } from './components/types/common'; // Import Song Type
import { Terminal, AlertCircle, Info, Activity, X, RefreshCw, Music } from 'lucide-react';

const PROLOGUE_SCRIPT: CutsceneScript = {
    id: 'prologue',
    nextView: ViewState.PLANE_STAGE,
    lines: [
        { speaker: 'RUMI', text: 'Okay, this is our biggest show yet!', emotion: 'HAPPY' },
        { speaker: 'ZOEY', text: 'The most songs!', emotion: 'HAPPY' },
        { speaker: 'MIRA', text: 'The most moves.', emotion: 'HAPPY' },
        { speaker: 'RUMI', text: 'Which means... the most carb loading!', emotion: 'HAPPY' },
        { speaker: 'RUMI/MIRA/ZOEY', text: 'FOR THE FANS.', emotion: 'PROUD' },
        { speaker: 'ALL', text: 'Happy fans, happy Honmoon!', emotion: 'HAPPY' },
        { speaker: 'ZOEY', text: 'Wait, there’s no water in these?', emotion: 'NEUTRAL' },
        { speaker: 'RUMI', text: 'Umm, excuse me, Miss?', emotion: 'NEUTRAL' },
        { speaker: 'FLIGHT ATTENDANT', text: 'Yes, Miss Hunter-ix?', emotion: 'NEUTRAL' },
        { speaker: 'RUMI', text: 'Late?', emotion: 'NEUTRAL' },
        { speaker: 'RUMI', text: 'Keep your shirt on, Bobby. We’ll be there in three.', emotion: 'NEUTRAL' },
        { speaker: 'MIRA', text: 'GAH!!! I didn’t even get to finish my ramyun!', emotion: 'DANGER' },
        { speaker: 'ZOEY', text: 'THEY WILL FEEL MY WRATH!', emotion: 'DANGER' },
        { speaker: 'RUMI', text: 'Yeah we don’t have time- you’re a demon, right?', emotion: 'NEUTRAL' },
        { speaker: 'FLIGHT ATTENDANT', text: '(nervous) W-What do you mean??', emotion: 'DANGER' },
        { speaker: 'RUMI', text: 'The rest of you can come out. We’re in a hurry.', emotion: 'NEUTRAL' },
        { speaker: 'MIRA', text: 'Aww... you got the patterns. Now you gotta die.', emotion: 'DANGER' },
        { speaker: 'PILOT DEMON', text: '(frustrated) Your fans! We’re gonna eat your fans!', emotion: 'DANGER' },
        { speaker: 'RUMI/MIRA/ZOEY', text: 'WOAH! UH OH! No, no, no. No thank you!', emotion: 'DANGER' },
        { speaker: 'RUMI', text: 'Not our fans...', emotion: 'DANGER' },
        { speaker: 'ZOEY', text: 'When you mess with our fans-', emotion: 'DANGER' },
        { speaker: 'MIRA', text: '-we need to make it hurt.', emotion: 'DANGER' },
    ]
};

const AudioDiagnostics: React.FC<{ onClose: () => void, topOffset: number }> = ({ onClose, topOffset }) => {
    const [state, setState] = useState(MusicManager.getContextState());

    useEffect(() => {
        const interval = setInterval(() => {
            setState(MusicManager.getContextState());
        }, 200);
        return () => clearInterval(interval);
    }, []);

    const signalColor = state.level > -10 ? 'bg-red-500' : state.level > -30 ? 'bg-green-400' : 'bg-blue-400';
    const signalWidth = Math.max(0, (state.level + 100) / 100 * 100);

    return (
        <div
            className="fixed right-2 md:right-4 z-[500] bg-slate-950/95 border border-slate-800 p-4 rounded-xl backdrop-blur-md shadow-2xl font-mono text-[10px] w-72 flex flex-col gap-3 transition-all"
            style={{ top: `${topOffset}px` }}
        >
            <div className="flex justify-between items-center border-b border-slate-800 pb-1 mb-1">
                <span className="text-slate-500 font-bold uppercase flex items-center gap-2"><Activity size={12} className="text-cyan-400" /> Audio Signal</span>
                <div className="flex items-center gap-3">
                    <span className={state.context === 'running' ? 'text-green-400' : 'text-red-400 animate-pulse'}>{state.context.toUpperCase()}</span>
                    <button onClick={onClose} className="text-slate-600 hover:text-white transition-colors"><X size={14} /></button>
                </div>
            </div>

            <div className="space-y-1">
                <div className="flex justify-between text-slate-500 uppercase text-[8px] font-bold">
                    <span>Peak Level</span>
                    <span>{Math.round(state.level)} dB</span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className={`h-full transition-all duration-75 ${signalColor}`} style={{ width: `${signalWidth}%` }}></div>
                </div>
            </div>

            <div className="flex flex-col gap-1 border-t border-slate-800/50 pt-2">
                <div className="flex justify-between">
                    <span className="text-slate-500">{state.activeMp3 ? "Track File:" : "Last Note:"}</span>
                    <span className="text-pink-400 text-right truncate">{state.activeMp3 || state.lastNote}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-slate-500">BPM/PPQ:</span>
                    <span className="text-white text-right">{state.bpm}/{state.ppq}</span>
                </div>
            </div>

            <div className="flex flex-col gap-1.5 mt-1 pt-2 border-t border-slate-800/50">
                <button
                    onClick={() => MusicManager.forceResume()}
                    className="w-full py-2 bg-slate-800 hover:bg-slate-700 text-white rounded border border-slate-600 uppercase font-bold text-[9px] flex items-center justify-center gap-2"
                >
                    Resume Audio
                </button>
                <button
                    onClick={() => MusicManager.hardReboot()}
                    className="w-full py-2 bg-red-900/40 hover:bg-red-800/60 text-red-200 rounded border border-red-500/50 uppercase font-black text-[9px] flex items-center justify-center gap-2 group"
                >
                    <RefreshCw size={10} className="group-active:animate-spin" />
                    Hard Reboot Engine
                </button>
            </div>
            <p className="text-[8px] text-slate-600 italic">Reboot if audio cuts out completely.</p>
        </div>
    );
};

const getSingerColor = (singer: string | undefined) => {
    switch (singer) {
        case 'MIRA': return 'text-pink-500 drop-shadow-[0_0_5px_rgba(236,72,153,0.8)]';
        case 'ZOEY': return 'text-cyan-400 drop-shadow-[0_0_5px_rgba(34,211,238,0.8)]';
        case 'RUMI': return 'text-purple-500 drop-shadow-[0_0_5px_rgba(168,85,247,0.8)]';
        case 'ALL': return 'text-yellow-400 drop-shadow-[0_0_5px_rgba(250,204,21,0.8)]';
        default: return 'text-slate-300';
    }
};

const getSingerBarColor = (singer: string | undefined) => {
    switch (singer) {
        case 'MIRA': return 'bg-pink-500 shadow-[0_0_15px_#ec4899]';
        case 'ZOEY': return 'bg-cyan-400 shadow-[0_0_15px_#22d3ee]';
        case 'RUMI': return 'bg-purple-500 shadow-[0_0_15px_#a855f7]';
        case 'ALL': return 'bg-yellow-400 shadow-[0_0_15px_#facc15]';
        default: return 'bg-slate-500';
    }
};

const App: React.FC = () => {
    const [view, setView] = useState<ViewState>(ViewState.MENU);
    const [activeScript, setActiveScript] = useState<CutsceneScript | null>(null);
    const [bobbyTotalEarnings, setBobbyTotalEarnings] = useState(0);
    const [pendingCity, setPendingCity] = useState<string | null>(null);
    const [isMuted, setIsMuted] = useState(false);
    const [isPracticeMode, setIsPracticeMode] = useState(false);
    const [isBathhouseFight, setIsBathhouseFight] = useState(false); // NEW

    const [logs, setLogs] = useState<{ msg: string, type: 'info' | 'error', id: number }[]>([]);

    const [showConsole, setShowConsole] = useState(false);
    const [showDiagnostics, setShowDiagnostics] = useState(false);
    const [lyricData, setLyricData] = useState<LyricContext | null>(null);

    const [currentSongIndex, setCurrentSongIndex] = useState(0);
    const [isMusicPlaying, setIsMusicPlaying] = useState(false);
    const [isShuffle, setIsShuffle] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const [mixMode, setMixMode] = useState<MusicMixMode>('MP3');

    // Notification State
    const [notifications, setNotifications] = useState<Notification[]>([]);

    // Sussie Messenger State
    const [isSussieOpen, setIsSussieOpen] = useState(false);
    const [sussieMissions, setSussieMissions] = useState<Mission[]>([]);
    const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

    const [player, setPlayer] = useState<PlayerProfile>({
        name: 'HUNTR/X',
        cash: INITIAL_PLAYER_CASH,
        fans: 10000,
        rank: IdolRank.TRAINEE,
        skills: { songwriting: 25, choreography: 20, marketing: 30 },
        group: { ...INITIAL_HUNTRIX_CONFIG },
        merchStock: 125,
        currentCity: 'SEOUL',
        activeContracts: [],
        mysticWards: 1,
        tonicCount: 0,
        hasAchievedGoldenHonmoon: false,
        conqueredCities: [],
        unlockedCostumes: ['DEFAULT']
    });

    const addNotification = useCallback((message: string, type: Notification['type'] = 'info') => {
        const id = Date.now();
        setNotifications(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }, 5000);
    }, []);

    const handleNextSong = useCallback(() => {
        if (isShuffle) {
            let nextIndex = Math.floor(Math.random() * SONGS.length);
            if (nextIndex === currentSongIndex && SONGS.length > 1) {
                nextIndex = (nextIndex + 1) % SONGS.length;
            }
            setCurrentSongIndex(nextIndex);
        } else {
            setCurrentSongIndex(p => (p + 1) % SONGS.length);
        }
        setLyricData(null);
    }, [isShuffle, currentSongIndex]);

    useEffect(() => {
        let internalCounter = 0;
        MusicManager.onError = (msg) => {
            internalCounter++;
            const id = `${Date.now()}-${internalCounter}-${Math.random().toString(36).substr(2, 5)}`;
            setLogs(prev => [{ msg, type: 'error' as const, id }, ...prev].slice(0, 50));
        };
        MusicManager.onStatus = (msg) => {
            internalCounter++;
            const id = `${Date.now()}-${internalCounter}-${Math.random().toString(36).substr(2, 5)}`;
            setLogs(prev => [{ msg, type: 'info' as const, id }, ...prev].slice(0, 100));
        };
        MusicManager.onLyricUpdate = (data) => {
            setLyricData(data);
        };
        MusicManager.onSongEnd = handleNextSong;

        return () => {
            MusicManager.onError = null;
            MusicManager.onStatus = null;
            MusicManager.onLyricUpdate = null;
            MusicManager.onSongEnd = null;
        };
    }, [handleNextSong]);

    const shouldLoop = false;

    useEffect(() => {
        if (gameStarted) {
            // Rule Implementation:
            // Trainee (0): Embedded Sequence loop (.ts)
            // Rookie (1): MIDI file layer (.mid)
            // Idol (2): Studio MP3 (.mp3)
            // Superstar+ (3): User Choice
            let activeMode: MusicMixMode = mixMode;
            if (player.rank === IdolRank.TRAINEE) activeMode = 'SEQUENCE';
            else if (player.rank === IdolRank.ROOKIE) activeMode = 'MIDI';
            else if (player.rank === IdolRank.IDOL) activeMode = 'MP3';

            MusicManager.playSong(SONGS[currentSongIndex].id, shouldLoop, activeMode);
        }
    }, [currentSongIndex, gameStarted, shouldLoop, player.rank, mixMode]);

    // Continuous Revenue Stream based on Rank & Fans
    useEffect(() => {
        if (!gameStarted) return;
        const revenueInterval = setInterval(() => {
            const rankData = RANKS[player.rank] || RANKS[IdolRank.TRAINEE];
            // Revenue formula: Base passive income * rank multiplier + small percentage of fans
            const passiveCash = Math.floor(rankData.passiveIncome + (player.fans * 0.001));

            setPlayer(prev => ({
                ...prev,
                cash: prev.cash + passiveCash
            }));

            // Optional: Tiny popup or just let it tick up in the dashboard
        }, 5000); // Every 5 seconds

        return () => clearInterval(revenueInterval);
    }, [gameStarted, player.rank, player.fans]);

    // Sussie Mission Delivery Timer
    useEffect(() => {
        if (!gameStarted) return;

        const missionInterval = setInterval(() => {
            if (isSussieOpen || sussieMissions.length >= 3) return;

            const unlockedCities = Object.keys(CITIES).filter(cityKey => CITIES[cityKey].minRank <= player.rank);

            const availablePool = MISSION_POOL.filter(
                p =>
                    !sussieMissions.some(m => m.id === p.id) &&
                    !player.activeContracts.some(c => c.id === p.id) &&
                    unlockedCities.includes(p.destination)
            );

            if (availablePool.length > 0) {
                const newMission = availablePool[Math.floor(Math.random() * availablePool.length)];
                setSussieMissions(prev => [...prev, newMission]);
                setHasUnreadMessages(true);
                setLogs(prev => [{ msg: `A message from Jinu has arrived.`, type: 'info', id: Date.now() }, ...prev]);
            }

        }, 30000);

        return () => clearInterval(missionInterval);
    }, [gameStarted, player.activeContracts, sussieMissions, isSussieOpen, player.rank, addNotification]);

    const handleStartGame = async () => {
        await MusicManager.initialize();
        MusicManager.toggleMute(isMuted);
        setIsMusicPlaying(true);
        setGameStarted(true);
        setPendingCity('SEOUL');
        setActiveScript(PROLOGUE_SCRIPT);
        setView(ViewState.CUTSCENE);
    };

    const toggleMute = () => {
        const newState = !isMuted;
        setIsMuted(newState);
        MusicManager.toggleMute(newState);
    };

    const handleTogglePlayMusic = () => {
        if (isMusicPlaying) {
            MusicManager.pauseMusic();
            setLyricData(null);
        }
        else MusicManager.resumeMusic();
        setIsMusicPlaying(!isMusicPlaying);
    };

    const handleOpenSussie = () => {
        setIsSussieOpen(true);
        setHasUnreadMessages(false);
    };

    const handleCloseSussie = () => {
        setIsSussieOpen(false);
    };

    const handleAcceptMission = (mission: Mission) => {
        if (player.activeContracts.some(c => c.id === mission.id)) return;
        setPlayer(prev => ({
            ...prev,
            activeContracts: [...prev.activeContracts, mission]
        }));
    };

    const handleCompleteMission = (missionId: string) => {
        const mission = player.activeContracts.find(c => c.id === missionId);
        if (!mission) return;

        // Apply Rank Multiplier to Rewards
        const rankData = RANKS[player.rank] || RANKS[IdolRank.TRAINEE];
        const multiplier = rankData?.multiplier || 1;
        const cashReward = (mission?.reward || 0) * multiplier;
        const fanReward = mission.fanReward * multiplier;

        setPlayer(prev => ({
            ...prev,
            cash: prev.cash + cashReward,
            fans: prev.fans + fanReward,
            activeContracts: prev.activeContracts.filter(c => c.id !== missionId)
        }));

        addNotification(`MISSION COMPLETE: ${mission.title}\nBonus Payout: $${cashReward.toLocaleString()}, Bonus Fans: +${fanReward.toLocaleString()}`, 'success');
    };

    const handlePlayerUpdate = (updates: Partial<PlayerProfile>) => {
        setPlayer(prevPlayer => {
            const currentUpdates = { ...updates };
            if (currentUpdates.cash !== undefined) {
                const cashChange = currentUpdates.cash - prevPlayer.cash;
                if (cashChange > 0) {
                    const commission = Math.floor(cashChange * BOBBY_COMMISSION);
                    setBobbyTotalEarnings(prevEarnings => prevEarnings + commission);
                }
            }
            return { ...prevPlayer, ...currentUpdates };
        });
    };

    const handleUseTonic = () => {
        setPlayer(prev => ({
            ...prev,
            tonicCount: Math.max(0, prev.tonicCount - 1)
        }));
    };

    const handleUseWard = () => {
        setPlayer(prev => ({
            ...prev,
            mysticWards: Math.max(0, prev.mysticWards - 1)
        }));
    };

    const handleReturnFromPlaneCombat = (winnings: number, won: boolean) => {
        if (won) {
            setView(ViewState.SKY_DROP);
        } else {
            setView(ViewState.PLANE_STAGE);
        }
    };

    const handleReturnFromCombat = (winnings: number, won: boolean, gainedSkills?: Partial<ProducerSkills>, enemiesDefeated: number = 0, achievedGoldenHonmoon?: boolean) => {
        const combatCity = pendingCity || player.currentCity;
        const cityData = CITIES[combatCity];
        const cityMultiplier = cityData ? cityData.difficultyMultiplier : 1.0;
        const rankData = RANKS[player.rank] || RANKS[IdolRank.TRAINEE];
        const rankMultiplier = rankData?.multiplier || 1;

        if (isBathhouseFight) {
            // Notification removed per request
            setIsBathhouseFight(false);
        }

        if (isPracticeMode) {
            if (won) {
                setPlayer(prev => ({
                    ...prev,
                    skills: {
                        songwriting: Math.min(99, prev.skills.songwriting + 1),
                        choreography: Math.min(99, prev.skills.choreography + 1),
                        marketing: Math.min(99, prev.skills.marketing + 1)
                    }
                }));
                addNotification("Rehearsal Successful! The group feels more confident. (Skills +1)", 'success');
            } else {
                addNotification("Rehearsal cut short. No penalties for practice!", 'info');
            }
            setIsPracticeMode(false);
            setView(ViewState.CITY);
            return;
        }

        if (!won) {
            setPlayer(prev => ({ ...prev, fans: Math.max(0, prev.fans - 50000) }));
            setPendingCity(null);
            setView(ViewState.CITY);
            return;
        }

        // --- MISSION COMPLETION LOGIC ---
        let missionCashBonus = 0;
        let missionFanBonus = 0;
        const completedMissionIds: string[] = [];

        player.activeContracts.forEach(mission => {
            if (mission.destination === combatCity && mission.type === 'TOUR_DATE') {
                missionCashBonus += (mission?.reward || 0) * rankMultiplier;
                missionFanBonus += mission.fanReward * rankMultiplier;
                completedMissionIds.push(mission.id);
                addNotification(`MISSION COMPLETE: ${mission.title}\nBonus Payout: $${((mission?.reward || 0) * rankMultiplier).toLocaleString()}, Bonus Fans: +${((mission?.fanReward || 0) * rankMultiplier).toLocaleString()}`, 'success');
            }
        });
        const updatedContracts = player.activeContracts.filter(c => !completedMissionIds.includes(c.id));
        // --- END MISSION LOGIC ---

        const baseWinnings = Math.floor(winnings * 5000 * cityMultiplier * rankMultiplier);
        const winBonus = Math.floor(VICTORY_BONUS_CASH * cityMultiplier * 0.2 * rankMultiplier);
        const isNewConquest = !player.conqueredCities.includes(combatCity);

        // --- MERCH REFILL LOGIC ---
        // Refill stock based on enemies defeated
        const collectedScraps = Math.floor(enemiesDefeated * (2 + Math.random() * 3));

        setPlayer(prev => ({
            ...prev,
            cash: prev.cash + baseWinnings + winBonus + missionCashBonus,
            fans: prev.fans + Math.floor((enemiesDefeated * 1000 * cityMultiplier * rankMultiplier)) + missionFanBonus,
            conqueredCities: isNewConquest ? [...prev.conqueredCities, combatCity] : prev.conqueredCities,
            currentCity: combatCity,
            activeContracts: updatedContracts,
            merchStock: prev.merchStock + collectedScraps
        }));

        // Notification removed per request

        setPendingCity(null);
        setView(ViewState.CITY);
    };

    const isCutscene = view === ViewState.CUTSCENE;
    const isSkydiving = view === ViewState.SKY_DROP;
    const isStage = view === ViewState.STAGE || view === ViewState.PLANE_STAGE;

    const topHUDOffset = (gameStarted && !isCutscene && !isSkydiving) ? 120 : 0;

    return (
        <div className="w-screen h-screen text-slate-200 flex flex-col font-sans bg-black overflow-hidden relative">

            {isSussieOpen && (
                <SussieMessenger
                    player={player}
                    missions={sussieMissions}
                    onClose={handleCloseSussie}
                    onAccept={handleAcceptMission}
                />
            )}

            {showDiagnostics && (
                <AudioDiagnostics onClose={() => setShowDiagnostics(false)} topOffset={topHUDOffset + 10} />
            )}

            {showConsole && logs.length > 0 && !isCutscene && !isSkydiving && (
                <div
                    className="fixed right-2 md:right-4 z-[500] w-72 bg-black/90 border border-slate-800 p-3 rounded-lg backdrop-blur-md shadow-2xl font-mono text-[10px] transition-all duration-500"
                    style={{ top: `${topHUDOffset + 10}px` }}
                >
                    <div className="flex justify-between items-center mb-2 border-b border-slate-800 pb-1">
                        <span className="flex items-center gap-1.5 text-slate-500 font-bold uppercase"><Terminal size={12} /> System Monitor</span>
                        <button onClick={() => setShowConsole(false)} className="text-slate-600 hover:text-white transition-colors"><X size={14} /></button>
                    </div>
                    <div className="space-y-1 max-h-40 overflow-y-auto custom-scrollbar">
                        {logs.map(log => (
                            <div key={log.id} className={`flex gap-2 ${log.type === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>
                                {log.type === 'error' ? <AlertCircle size={10} className="shrink-0 mt-0.5" /> : <Info size={10} className="shrink-0 mt-0.5" />}
                                <span className="leading-tight">{log.msg}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {view !== ViewState.MENU && !isCutscene && !isSkydiving && (
                <Dashboard player={player} bobbyEarnings={bobbyTotalEarnings} notifications={notifications}>
                    <MusicPlayer
                        currentSong={SONGS[currentSongIndex]}
                        isPlaying={isMusicPlaying}
                        isMuted={isMuted}
                        isShuffle={isShuffle}
                        playerRank={player.rank}
                        mixMode={mixMode}
                        onTogglePlay={handleTogglePlayMusic}
                        onToggleMute={toggleMute}
                        onToggleShuffle={() => setIsShuffle(!isShuffle)}
                        onToggleMixMode={() => {
                            const modes: MusicMixMode[] = ['MP3', 'SEQUENCE', 'MIDI'];
                            const next = modes[(modes.indexOf(mixMode) + 1) % modes.length];
                            setMixMode(next);
                            const displayLabel = next === 'MIDI' ? 'MID' : next === 'SEQUENCE' ? 'SEQ' : 'MP3';
                            addNotification(`Audio Resonance Switched: ${displayLabel}`, 'info');
                        }}
                        onNext={handleNextSong}
                        onPrev={() => { setCurrentSongIndex(p => (p - 1 + SONGS.length) % SONGS.length); setLyricData(null); }}
                    />
                    <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-800">
                        <button
                            onClick={() => setShowDiagnostics(!showDiagnostics)}
                            className={`p-1.5 rounded transition-all ${showDiagnostics ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-500 hover:text-white'}`}
                            title="Toggle Audio Signal"
                        >
                            <Activity size={16} />
                        </button>
                        <button
                            onClick={() => setShowConsole(!showConsole)}
                            className={`p-1.5 rounded transition-all ${showConsole ? 'bg-pink-500/20 text-pink-400' : 'text-slate-500 hover:text-white'}`}
                            title="Toggle System Monitor"
                        >
                            <Terminal size={16} />
                        </button>
                    </div>
                </Dashboard>
            )}

            <main className="flex-1 relative overflow-hidden flex flex-col items-center justify-center p-2 md:p-4">
                {(() => {
                    // Handle special prologue case first to render stage background
                    if (view === ViewState.CUTSCENE && activeScript?.id === 'prologue') {
                        return (
                            <>
                                <Stage
                                    playerSquad={player.group}
                                    playerSkills={player.skills}
                                    producerName={player.name}
                                    tonicCount={player.tonicCount}
                                    mysticWards={player.mysticWards}
                                    currentCity={"IN_FLIGHT"}
                                    unlockedCostumes={player.unlockedCostumes}
                                    isPracticeMode={false}
                                    isPlaneStage={true}
                                    isBackgroundOnly={true}
                                    hideWeapon={true}
                                    onUseTonic={handleUseTonic}
                                    onUseWard={handleUseWard}
                                    onUpdatePlayer={handlePlayerUpdate}
                                    onExit={handleReturnFromPlaneCombat}
                                    song={SONGS.find(s => s.id === 'HOW_ITS_DONE') || SONGS[0]}
                                />
                                <div className="absolute inset-0 z-10 pointer-events-none">
                                    <div className="pointer-events-auto w-full h-full flex flex-col items-center justify-center">
                                        <CutsceneView script={activeScript} onComplete={setView} transparentBg={true} />
                                    </div>
                                </div>
                            </>
                        );
                    }

                    switch (view) {
                        case ViewState.MENU: return <MainMenu onStart={handleStartGame} />;
                        case ViewState.CUTSCENE: return activeScript ? <CutsceneView script={activeScript} onComplete={setView} /> : null;
                        case ViewState.CITY:
                            console.log('DEBUG: View is CITY, currentCity:', player.currentCity);
                            return <CityHub onNavigate={(v) => { if (v === ViewState.STAGE) setIsPracticeMode(false); setView(v); }} cityKey={player.currentCity} isConquered={player.conqueredCities.includes(player.currentCity)} hasUnreadMessages={hasUnreadMessages} onOpenSussie={handleOpenSussie} />;
                        case ViewState.TOUR_MAP: return <TourMap player={player} onTravel={(dest) => { handlePlayerUpdate({ cash: player.cash - 10000, currentCity: dest }); setView(ViewState.CITY); }} onExit={() => setView(ViewState.CITY)} />;
                        case ViewState.SKY_DROP: return <SkyDrop destination={pendingCity || player.currentCity} onComplete={() => setView(ViewState.STAGE)} />;
                        case ViewState.PRACTICE_ROOM: return <PracticeRoom player={player} onUpdatePlayer={handlePlayerUpdate} onNavigate={(v) => { if (v === ViewState.STAGE) setIsPracticeMode(true); setView(v); }} onExit={() => setView(ViewState.CITY)} onNotify={addNotification} />;
                        case ViewState.VARIETY_SHOW: return <VarietyShow player={player} onUpdatePlayer={handlePlayerUpdate} onExit={() => setView(ViewState.CITY)} onNotify={addNotification} />;
                        case ViewState.MERCH_SHOP: return <MerchShop player={player} updatePlayer={handlePlayerUpdate} onExit={() => setView(ViewState.CITY)} onNotify={addNotification} />;
                        case ViewState.CAFE: return <IdolCafe player={player} updatePlayer={handlePlayerUpdate} onExit={() => setView(ViewState.CITY)} onNotify={addNotification} onCompleteMission={handleCompleteMission} />;
                        case ViewState.BATHHOUSE: return <Bathhouse player={player} updatePlayer={handlePlayerUpdate} onExit={() => setView(ViewState.CITY)} onFight={() => { setIsBathhouseFight(true); setView(ViewState.STAGE); }} onNotify={addNotification} />;
                        case ViewState.HEALER_CLINIC: return <HealerClinic player={player} updatePlayer={handlePlayerUpdate} onExit={() => setView(ViewState.CITY)} />;
                        case ViewState.SPICY_CHALLENGE: return <SpicyChallenge player={player} onUpdatePlayer={handlePlayerUpdate} onExit={() => setView(ViewState.CITY)} onNotify={addNotification} />;
                        case ViewState.RECORD_LABEL: return <RecordLabel player={player} onUpdatePlayer={handlePlayerUpdate} onExit={() => setView(ViewState.CITY)} onNotify={addNotification} />;
                        case ViewState.STREET_FOOD:
                            return <StreetFood
                                onNavigate={(v) => setView(v)}
                                cityKey={player.currentCity}
                                cash={player.cash}
                                fans={player.fans}
                                playerRank={player.rank}
                                onUpdateStats={({ cash, fans }) =>
                                    handlePlayerUpdate({ cash: player.cash + cash, fans: player.fans + fans })
                                }
                            />;
                        case ViewState.STAGE:
                        case ViewState.PLANE_STAGE:
                            return (
                                <Stage
                                    playerSquad={player.group}
                                    playerSkills={player.skills}
                                    producerName={player.name}
                                    tonicCount={player.tonicCount}
                                    mysticWards={player.mysticWards}
                                    currentCity={view === ViewState.PLANE_STAGE ? "IN_FLIGHT" : player.currentCity}
                                    unlockedCostumes={player.unlockedCostumes}
                                    isPracticeMode={isPracticeMode}
                                    song={view === ViewState.PLANE_STAGE ? (SONGS.find(s => s.id === 'TAKEDOWN') || SONGS[0]) : SONGS[currentSongIndex]}
                                    isPlaneStage={view === ViewState.PLANE_STAGE}
                                    isBathhouseFight={isBathhouseFight}
                                    onUseTonic={handleUseTonic}
                                    onUseWard={handleUseWard}
                                    onUpdatePlayer={handlePlayerUpdate}
                                    onExit={view === ViewState.PLANE_STAGE ? handleReturnFromPlaneCombat : handleReturnFromCombat}
                                />
                            );
                        default: return null;
                    }
                })()}
            </main>

            {gameStarted && (
                <div className="shrink-0 z-[100] bg-black border-t border-white/20 p-2 flex flex-col items-center justify-center h-24 md:h-28">
                    {lyricData?.current?.text ? (
                        <div className="flex flex-col gap-0.5 text-center max-w-[95vw] md:max-w-[70vw] w-full items-center justify-center animate-in fade-in duration-300">
                            {/* Previous Line */}
                            <div className={`text-xs md:text-sm italic font-bold h-4 line-clamp-1 transition-all ${getSingerColor(lyricData.prev.singer)} opacity-50`}>
                                {lyricData.prev.text}
                            </div>

                            {/* Current Line */}
                            <div className="relative w-full flex flex-col items-center">
                                <div className={`text-lg md:text-2xl font-heading font-black uppercase tracking-tight leading-none min-h-[24px] flex items-center justify-center transition-all ${getSingerColor(lyricData.current.singer)} drop-shadow-md`}>
                                    {lyricData.current.text}
                                </div>

                                {/* Progress Bar & Singer Name Row */}
                                <div className="flex items-center justify-center gap-2 w-full mt-1">
                                    {lyricData.current.singer && (
                                        <div className={`text-[9px] md:text-[10px] font-black uppercase tracking-widest ${getSingerColor(lyricData.current.singer)} whitespace-nowrap`}>
                                            {lyricData.current.singer}
                                        </div>
                                    )}
                                    <div className="w-1/3 h-1 bg-slate-800/50 rounded-full overflow-hidden backdrop-blur-sm">
                                        <div
                                            className={`h-full relative transition-all duration-300 ${getSingerBarColor(lyricData.current.singer)}`}
                                            style={{ animation: 'lyricProgress 2s linear forwards' }}
                                            key={lyricData.current.text}
                                        ></div>
                                    </div>
                                </div>
                            </div>

                            {/* Next Line */}
                            <div className={`text-sm md:text-base font-bold h-5 line-clamp-1 transition-all ${getSingerColor(lyricData.next.singer)} opacity-40`}>
                                {lyricData.next.text}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center opacity-20">
                            <Music size={24} className="text-slate-500 animate-pulse" />
                        </div>
                    )}
                </div>
            )}

            <style>{`
                @keyframes lyricProgress {
                    from { width: 0%; }
                    to { width: 100%; }
                }
            `}</style>
        </div>
    );
};

export default App;